/**
 * TradeCluster.js
 *
 * Component for rendering a cluster of trades when zoomed out.
 *
 * WHY THIS EXISTS:
 * - When zoomed out, individual trade arrows overlap and become unreadable
 * - This component shows a single marker with the count of trades in that area
 * - Clicking zooms in to show individual trades
 * - Tooltip shows summary of trades in the cluster
 *
 * APPEARANCE:
 * - Circle with count displayed inside
 * - Color indicates predominant trade direction (green for buys, red for sells)
 * - Subtle border and shadow for visibility
 */
import React, { useState, useCallback } from 'react';

// Colors matching the main chart's marker colors
const COLORS = {
  buy: '#22c55e',       // Green for buys
  buyBg: '#166534',
  sell: '#ef4444',      // Red for sells
  sellBg: '#991b1b',
  text: '#ffffff',
};

/**
 * Determine cluster color based on buy/sell majority.
 * Simple rule: more buys = green, more sells = red. No mixed/blue/split.
 */
function getClusterColor(buyCount, sellCount) {
  if (buyCount >= sellCount) {
    return { fill: COLORS.buyBg, stroke: COLORS.buy };
  }
  return { fill: COLORS.sellBg, stroke: COLORS.sell };
}

/**
 * Format cluster summary for tooltip
 */
function getClusterSummary(cluster) {
  const parts = [];
  if (cluster.buyCount > 0) {
    parts.push(`${cluster.buyCount} buy${cluster.buyCount !== 1 ? 's' : ''}`);
  }
  if (cluster.sellCount > 0) {
    parts.push(`${cluster.sellCount} sell${cluster.sellCount !== 1 ? 's' : ''}`);
  }
  if (cluster.markerCount > 0) {
    parts.push(`${cluster.markerCount} marker${cluster.markerCount !== 1 ? 's' : ''}`);
  }
  return parts.join(', ');
}

/**
 * TradeCluster - Renders a cluster marker for grouped trades
 *
 * @param {Object} props
 * @param {Object} props.cluster - Cluster object from useClusteredSignals
 * @param {Function} props.onClick - Called when cluster is clicked (for zooming)
 * @param {Function} props.onHover - Called when cluster is hovered
 * @param {Function} props.onMouseLeave - Called when mouse leaves cluster
 */
export function TradeCluster({
  cluster,
  onClick,
  onHover,
  onMouseLeave,
}) {
  const [isHovered, setIsHovered] = useState(false);

  const handleMouseEnter = useCallback((e) => {
    setIsHovered(true);
    if (onHover) {
      onHover(e, cluster);
    }
  }, [cluster, onHover]);

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
    if (onMouseLeave) {
      onMouseLeave();
    }
  }, [onMouseLeave]);

  const handleClick = useCallback((e) => {
    if (onClick) {
      onClick(e, cluster);
    }
  }, [cluster, onClick]);

  const { centerX, centerY, buyCount, sellCount, signals } = cluster;
  const totalCount = signals.length;
  const colors = getClusterColor(buyCount, sellCount);

  // Size based on count — kept compact so clusters don't dominate the chart
  const baseSize = 11;
  const sizeMultiplier = Math.min(1 + Math.log10(totalCount) * 0.25, 1.6);
  const size = baseSize * sizeMultiplier;

  // Font size scales with marker size
  const fontSize = Math.max(8, Math.min(11, size * 0.65));

  // Format count for display (use K for large numbers)
  const displayCount = totalCount >= 1000
    ? `${(totalCount / 1000).toFixed(1)}k`
    : totalCount.toString();

  return (
    <g
      className="trade-cluster"
      transform={`translate(${centerX}, ${centerY})`}
      style={{ cursor: 'pointer' }}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Outer glow on hover */}
      {isHovered && (
        <circle
          cx={0}
          cy={0}
          r={size + 3}
          fill="none"
          stroke={colors.stroke}
          strokeWidth={1.5}
          opacity={0.35}
        />
      )}

      {/* Main circle — green for majority buys, red for majority sells */}
      <circle
        cx={0}
        cy={0}
        r={size}
        fill={colors.fill}
        fillOpacity={0.8}
        stroke={colors.stroke}
        strokeWidth={1.5}
      />

      {/* Count text */}
      <text
        x={0}
        y={0}
        textAnchor="middle"
        dominantBaseline="central"
        fill={COLORS.text}
        fontSize={fontSize}
        fontWeight="bold"
        fontFamily="system-ui, -apple-system, sans-serif"
        style={{ pointerEvents: 'none' }}
      >
        {displayCount}
      </text>

      {/* Tooltip hint - small arrow indicator */}
      {isHovered && (
        <text
          x={0}
          y={size + 12}
          textAnchor="middle"
          fill={colors.stroke}
          fontSize={9}
          fontFamily="system-ui, -apple-system, sans-serif"
          style={{ pointerEvents: 'none' }}
        >
          Click to zoom
        </text>
      )}
    </g>
  );
}

/**
 * TradeClusterTooltip - Tooltip content for cluster hover
 *
 * @param {Object} props
 * @param {Object} props.cluster - Cluster object
 * @param {number} props.x - Screen X position
 * @param {number} props.y - Screen Y position
 * @param {Function} props.onClose - Called when tooltip should close
 */
/**
 * TradeClusterTooltip - Informational overlay for cluster hover.
 *
 * Uses pointer-events:none so it NEVER blocks chart interaction (crosshairs,
 * clicks, zoom). Dismisses naturally when mouse leaves the cluster SVG element.
 */
export function TradeClusterTooltip({ cluster, x, y }) {
  if (!cluster) return null;

  const summary = getClusterSummary(cluster);
  const { buyCount, sellCount } = cluster;
  const colors = getClusterColor(buyCount, sellCount);

  return (
    <div
      className="cluster-tooltip"
      style={{
        position: 'fixed',
        left: x + 15,
        top: y - 10,
        transform: 'translateY(-100%)',
        backgroundColor: 'rgba(20, 20, 30, 0.95)',
        border: `1px solid ${colors.stroke}`,
        borderRadius: '8px',
        padding: '10px 14px',
        color: '#fff',
        fontSize: '13px',
        zIndex: 1000,
        minWidth: '140px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
        pointerEvents: 'none',
      }}
    >
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '6px',
        fontWeight: 'bold',
      }}>
        <span style={{
          display: 'inline-block',
          width: '10px',
          height: '10px',
          borderRadius: '50%',
          backgroundColor: colors.stroke,
        }} />
        {cluster.signals.length} Trades
      </div>

      <div style={{ color: '#ccc', fontSize: '12px' }}>
        {summary}
      </div>

      <div style={{
        marginTop: '8px',
        paddingTop: '8px',
        borderTop: '1px solid rgba(255,255,255,0.1)',
        color: '#888',
        fontSize: '11px',
      }}>
        Click to zoom in
      </div>
    </div>
  );
}

export default TradeCluster;
